import { Check, DotsThree, PaperPlaneTilt, Plus, Sparkle, X } from '@phosphor-icons/react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  addComment,
  blockUser,
  createReadingList,
  deleteComment,
  fetchChat,
  fetchComments,
  getListMembership,
  getReadingLists,
  reportComment,
  sendChat,
  toggleListItem,
  type ChatMsg,
} from '@/lib/api/user';
import { useAuth } from '@/lib/auth';
import { timeAgo } from '@/lib/format';
import { handleActionError, useRequireAuth } from '@/lib/hooks';
import { getUiLang, useT } from '@/lib/i18n';
import { ui } from '@/lib/ui';
import { Avatar, Drawer, Modal } from './primitives';

// ---------- Hikâyeye sor ----------

export function ChatDrawer({ contentId, title, onClose }: { contentId: string; title: string; onClose: () => void }) {
  const t = useT();
  const { user, premium } = useAuth();
  const [msgs, setMsgs] = useState<ChatMsg[]>([]);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const endRef = useRef<HTMLDivElement>(null);
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (user) fetchChat(user.id, contentId).then(setMsgs).catch(() => undefined);
  }, [user, contentId]);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [msgs, busy]);

  const send = async (raw: string) => {
    const q = raw.trim();
    if (!q || busy || !user) return;
    if (!premium) {
      ui.openPremium('chat');
      return;
    }
    setText('');
    const history = msgs;
    setMsgs([...history, { role: 'user', text: q }]);
    setBusy(true);
    try {
      const reply = await sendChat(user.id, contentId, history, q, getUiLang());
      setMsgs((m) => [...m, { role: 'assistant', text: reply }]);
    } catch (e) {
      setMsgs(history);
      setText(q);
      handleActionError(e);
    } finally {
      setBusy(false);
      taRef.current?.focus();
    }
  };

  const suggestions = [t('chat_s1'), t('chat_s2'), t('chat_s3')];
  return (
    <Drawer
      onClose={onClose}
      title={t('chat_title')}
      sub={t('chat_sub')}
      foot={
        <form
          className="composer"
          onSubmit={(e) => {
            e.preventDefault();
            void send(text);
          }}
        >
          <textarea
            ref={taRef}
            className="textarea"
            value={text}
            autoFocus
            rows={1}
            placeholder={t('chat_ph')}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void send(text);
              }
            }}
          />
          <button className="btn btn-primary" disabled={!text.trim() || busy} aria-label={t('chat_send')}>
            <PaperPlaneTilt size={18} weight="fill" />
          </button>
        </form>
      }
    >
      <p className="dim" style={{ fontSize: 13.5, marginBottom: 16 }}>
        “{title}”
      </p>
      {msgs.length === 0 && (
        <div style={{ display: 'grid', gap: 8 }}>
          {suggestions.map((s) => (
            <button key={s} className="btn btn-soft" style={{ justifyContent: 'flex-start', height: 'auto', padding: '11px 14px', whiteSpace: 'normal', textAlign: 'left' }} onClick={() => void send(s)}>
              <Sparkle size={16} style={{ flexShrink: 0 }} /> {s}
            </button>
          ))}
        </div>
      )}
      <div className="chat-list">
        {msgs.map((m, i) => (
          <div key={i} className={`bubble ${m.role}`}>
            {m.text}
          </div>
        ))}
        {busy && (
          <div className="bubble assistant muted" aria-live="polite">
            <span className="eq" style={{ marginRight: 8 }}>
              <i />
              <i />
              <i />
            </span>
            {t('chat_thinking')}
          </div>
        )}
        <div ref={endRef} />
      </div>
    </Drawer>
  );
}

// ---------- Liste seçici ----------

export function ListPicker({ contentId, onClose }: { contentId: string; onClose: () => void }) {
  const t = useT();
  const { user } = useAuth();
  const qc = useQueryClient();
  const listsQ = useQuery({ queryKey: ['lists', user?.id], enabled: !!user, queryFn: () => getReadingLists(user!.id) });
  const memQ = useQuery({ queryKey: ['membership', user?.id, contentId], enabled: !!user, queryFn: () => getListMembership(user!.id, contentId) });
  const [name, setName] = useState('');
  const [creating, setCreating] = useState(false);

  const toggle = async (listId: string) => {
    if (!user) return;
    const on = !memQ.data?.has(listId);
    qc.setQueryData<Set<string>>(['membership', user.id, contentId], (prev) => {
      const next = new Set(prev ?? []);
      if (on) next.add(listId);
      else next.delete(listId);
      return next;
    });
    try {
      await toggleListItem(user.id, listId, contentId, on);
      void qc.invalidateQueries({ queryKey: ['lists', user.id] });
    } catch (e) {
      void memQ.refetch();
      handleActionError(e);
    }
  };

  const create = async () => {
    if (!user || !name.trim()) return;
    setCreating(true);
    try {
      const id = await createReadingList(user.id, name, null);
      setName('');
      await toggleListItem(user.id, id, contentId, true);
      await Promise.all([listsQ.refetch(), memQ.refetch()]);
    } catch (e) {
      handleActionError(e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal onClose={onClose} labelledBy="lp-title">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2 id="lp-title">{t('story_add_list')}</h2>
        <button className="icon-btn icon-btn-sm" onClick={onClose} aria-label={t('close')}>
          <X size={16} />
        </button>
      </div>
      <div style={{ marginTop: 12 }}>
        {(listsQ.data ?? []).map((l) => {
          const on = memQ.data?.has(l.id) ?? false;
          return (
            <button key={l.id} className="list-row" style={{ width: '100%', textAlign: 'left' }} onClick={() => void toggle(l.id)}>
              <span style={{ fontSize: 20, width: 28, textAlign: 'center' }}>{l.emoji ?? '📚'}</span>
              <span className="grow">
                <h4>{l.name}</h4>
                <span className="muted" style={{ fontSize: 12.5 }}>
                  {t('lib_items', { n: l.item_count })}
                </span>
              </span>
              <span className={`tick`} style={{ width: 22, height: 22, borderRadius: 7, display: 'grid', placeItems: 'center', background: on ? 'var(--text)' : 'var(--surface-2)', color: 'var(--bg)' }}>
                {on && <Check size={13} weight="bold" />}
              </span>
            </button>
          );
        })}
        {listsQ.data?.length === 0 && <p className="muted" style={{ padding: '12px 0' }}>{t('lib_empty_lists')}</p>}
      </div>
      <form
        style={{ display: 'flex', gap: 8, marginTop: 16 }}
        onSubmit={(e) => {
          e.preventDefault();
          void create();
        }}
      >
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder={t('lib_list_name')} maxLength={60} />
        <button className="btn btn-primary" style={{ height: 44 }} disabled={!name.trim() || creating}>
          <Plus size={16} weight="bold" /> {t('lib_create')}
        </button>
      </form>
    </Modal>
  );
}

// ---------- Yorumlar ----------

export function Comments({ contentId }: { contentId: string }) {
  const t = useT();
  const { user, premium } = useAuth();
  const require = useRequireAuth();
  const q = useQuery({ queryKey: ['comments', contentId, user?.id], queryFn: () => fetchComments(contentId, user?.id) });
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [menu, setMenu] = useState<string | null>(null);

  const post = () =>
    require(async (uid) => {
      if (!premium) {
        ui.openPremium('comment');
        return;
      }
      if (!text.trim()) return;
      setBusy(true);
      try {
        await addComment(uid, contentId, text);
        setText('');
        await q.refetch();
      } catch (e) {
        handleActionError(e);
      } finally {
        setBusy(false);
      }
    });

  const act = async (fn: () => Promise<void>, msg?: string) => {
    setMenu(null);
    try {
      await fn();
      if (msg) ui.toast(msg);
      await q.refetch();
    } catch (e) {
      handleActionError(e);
    }
  };

  return (
    <section className="comments" aria-labelledby="comments-title">
      <h2 className="section-title" id="comments-title" style={{ marginBottom: 16 }}>
        {t('story_comments')} {q.data && q.data.length > 0 && <span className="muted">{q.data.length}</span>}
      </h2>
      <form
        style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}
        onSubmit={(e) => {
          e.preventDefault();
          post();
        }}
      >
        <textarea className="textarea" style={{ minHeight: 48, height: 48 }} value={text} onChange={(e) => setText(e.target.value)} onFocus={() => !user && require(() => undefined)} placeholder={t('story_comment_ph')} maxLength={4000} />
        <button className="btn btn-primary" style={{ height: 48 }} disabled={busy || !text.trim()}>
          {t('story_comment_send')}
        </button>
      </form>
      <div style={{ marginTop: 12 }}>
        {q.data?.length === 0 && <p className="muted" style={{ padding: '16px 0' }}>{t('story_comments_empty')}</p>}
        {q.data?.map((c) => (
          <div className="comment" key={c.id}>
            <Avatar name={c.author_name ?? '?'} />
            <div style={{ minWidth: 0 }}>
              <div className="meta">
                <span style={{ color: 'var(--text)', fontWeight: 700 }}>{c.author_name ?? 'Brifing'}</span>
                <span className="meta-sep" />
                <span>{timeAgo(c.created_at)}</span>
              </div>
              <p className="comment-body">{c.body}</p>
            </div>
            {user && (
              <div style={{ position: 'relative' }}>
                <button className="icon-btn icon-btn-sm" onClick={() => setMenu(menu === c.id ? null : c.id)} aria-label="…">
                  <DotsThree size={18} weight="bold" />
                </button>
                {menu === c.id && (
                  <div className="popover" style={{ right: 0, top: 36 }}>
                    {c.user_id === user.id ? (
                      <button className="menu-item btn-danger" onClick={() => void act(() => deleteComment(user.id, c.id))}>
                        {t('story_delete')}
                      </button>
                    ) : (
                      <>
                        <button className="menu-item" onClick={() => void act(() => reportComment(user.id, c.id), t('story_reported'))}>
                          {t('story_report')}
                        </button>
                        <button className="menu-item btn-danger" onClick={() => void act(() => blockUser(user.id, c.user_id))}>
                          {t('story_block')}
                        </button>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </section>
  );
}
